import { apiClient } from "../../../shared/api/client";
import type { Agent, AgentStatus } from "../../../entities/agent";

// ─── Profil agent ─────────────────────────────────────────────────────────────

export async function getAgent(agentId: string): Promise<Agent> {
  const { data } = await apiClient.get<Agent>(`/agents/${agentId}`);
  return data;
}

export type UpdateAgentPayload = {
  kycStatus?:    Agent["kycStatus"];
  qualityScore?: number;
  currentLoad?:  number;
  metadata?:     Record<string, unknown>;
};

export async function updateAgent(
  agentId: string,
  payload: UpdateAgentPayload,
): Promise<Agent> {
  const { data } = await apiClient.patch<Agent>(`/agents/${agentId}`, payload);
  return data;
}

// Création du profil agent — appelé une seule fois lors du premier login d'un
// utilisateur ayant le rôle AGENT. Le userId provient du JWT (sessionStore).
export async function createAgent(userId: string, metadata?: Record<string, unknown>): Promise<Agent> {
  const { data } = await apiClient.post<Agent>("/agents", { userId, metadata });
  return data;
}

export type ListAgentsParams = {
  status?:    AgentStatus;
  kycStatus?: Agent["kycStatus"];
  page?:      number;
  per_page?:  number;
};

// Liste des agents (GET /agents — openapi: filtres status/kycStatus/page/per_page,
// PAS de filtre userId). Comme l'Agent porte `id` ET `userId` distincts et que le
// contrat n'expose pas de lookup par userId, c'est le moyen documenté pour
// retrouver le profil d'un agent depuis son userId : lister puis filtrer côté
// client (voir resolveAgentId). `data` est l'array d'Agent après unwrap intercepteur.
export async function listAgents(params: ListAgentsParams = {}): Promise<Agent[]> {
  const { data } = await apiClient.get<Agent[]>("/agents", { params });
  return data;
}

/**
 * Profil de la Main à partir de son `sub` Keycloak — `GET /agents/by-user/{sub}`.
 *
 * ## Pourquoi cette route change tout
 * Il n'existe AUCUNE route `/agents/me`, et `{agentId}` n'est pas le `sub` :
 * c'est un UUID agent-service. Utiliser le `sub` dans une URL `/agents/{id}`
 * donne `404`, celui d'une autre Main `403`.
 *
 * Jusqu'ici l'app retrouvait son profil en PAGINANT `GET /agents` — jusqu'à
 * vingt pages de cent — puis en filtrant sur `userId` côté client. Un scan de
 * deux mille agents au démarrage, là où une requête suffit.
 *
 * À résoudre UNE FOIS au démarrage, puis à garder.
 *
 * ⚠️ Hors du contrat embarqué (openapi v2.1.0) : arrivée avec la note du
 * 2026-08-28. À revalider dès la régénération.
 */
export async function getAgentByUser(sub: string): Promise<Agent> {
  const { data } = await apiClient.get<Agent>(`/agents/by-user/${encodeURIComponent(sub)}`);
  return data;
}

// ─── Disponibilité ────────────────────────────────────────────────────────────

/**
 * Statuts que l'APPLICATION a le droit de poser.
 *
 * L'API en accepte quatre, mais deux sont des pièges :
 *   • `SUSPENDED` est un cul-de-sac — rien n'en sort automatiquement ;
 *   • `BUSY` posé à la main dérègle le compteur de charge. Le serveur le pose
 *     lui-même à l'ÉMISSION d'une offre ; ce n'est pas à l'app de le faire.
 */
export type SettableAgentStatus = Extract<AgentStatus, "OFFLINE" | "AVAILABLE">;

/**
 * Pose le statut de service — `POST /agents/{agentId}/availability`.
 *
 * ⚠️ C'est le SEUL appel qui réactive une Main. `POST /location` ne touche
 * jamais au statut : après quinze minutes sans position, le serveur bascule la
 * Main en `OFFLINE`, et reprendre le GPS ne la remettra pas en course. À
 * re-poster à chaque retour au premier plan et après toute reprise réseau.
 */
export async function setAgentAvailability(
  agentId: string,
  status:  SettableAgentStatus,
): Promise<Agent> {
  const { data } = await apiClient.post<Agent>(`/agents/${agentId}/availability`, { status });
  return data;
}

// ─── Position GPS ─────────────────────────────────────────────────────────────

export type AgentLocationPayload = {
  latitude:         number;
  longitude:        number;
  accuracyMeters?:  number;
};

export async function updateAgentLocation(
  agentId: string,
  payload: AgentLocationPayload,
): Promise<void> {
  await apiClient.post(`/agents/${agentId}/location`, payload);
}

// ─── Agents proches (matching côté client) ───────────────────────────────────

export type NearbyAgent = Agent & { distanceKm?: number };

export type NearbyAgentsParams = {
  latitude:   number;
  longitude:  number;
  radiusKm?:  number;
  limit?:     number;
};

export async function listNearbyAgents(params: NearbyAgentsParams): Promise<NearbyAgent[]> {
  const { data } = await apiClient.get<NearbyAgent[]>("/agents/nearby", { params });
  return data;
}
